import { motion } from "framer-motion";
import { Phone, Mail, Clock, CheckCircle, FileText, Mic, Video, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import ServiceSelector from "@/components/hire/ServiceSelector";
import ProceedingSelector from "@/components/hire/ProceedingSelector";
import FileUploader from "@/components/hire/FileUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const courtReportingOptions = [
  { id: "realtime", label: "Real-Time" },
  { id: "rough-drafts", label: "Rough Drafts" },
  { id: "expedites", label: "Expedites" },
  { id: "video-syncing", label: "Video Syncing" },
];

export default function HirePage() {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedCourtOptions, setSelectedCourtOptions] = useState([]);
  const [selectedProceeding, setSelectedProceeding] = useState("");
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [otherServiceText, setOtherServiceText] = useState("");
  const [otherLanguageText, setOtherLanguageText] = useState("");
  const [otherProceedingText, setOtherProceedingText] = useState("");
  const [nodFile, setNodFile] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", date: "", time: "", timeEstimate: "", notes: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleItem = (setter) => (item) => {
    setter(prev => prev.includes(item) ? prev.filter(s => s !== item) : [...prev, item]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { toast.error("File size must be less than 10MB"); return; }
      setNodFile(file);
    }
  };

  const removeFile = () => {
    setNodFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) { toast.error("Please fill in all required contact fields"); return; }
    if (!formData.timeEstimate) { toast.error("Please provide a time estimate"); return; }
    if (!nodFile) { toast.error("Please upload your Notice of Deposition"); return; }
    if (selectedServices.length === 0) { toast.error("Please select at least one service"); return; }
    if (selectedServices.includes("other-service") && !otherServiceText.trim()) { toast.error("Please specify your 'Other' service"); return; }
    if (selectedServices.includes("interpreter") && selectedLanguages.length === 0 && !otherLanguageText.trim()) { toast.error("Please select at least one language for interpretation"); return; }
    if (!selectedProceeding) { toast.error("Please select the type of proceeding"); return; }
    if (selectedProceeding === "other" && !otherProceedingText.trim()) { toast.error("Please specify the type of proceeding"); return; }

    setIsLoading(true);

    try {
      const allServices = [...selectedServices];
      if (selectedServices.includes("court-reporter") && selectedCourtOptions.length > 0) {
        allServices.push(`Court Reporter Options: ${selectedCourtOptions.join(", ")}`);
      }
      if (selectedServices.includes("interpreter")) {
        const langs = [...selectedLanguages];
        if (otherLanguageText.trim()) langs.push(`Other: ${otherLanguageText}`);
        allServices.push(`Interpreter Languages: ${langs.join(", ")}`);
      }
      if (selectedServices.includes("other-service") && otherServiceText.trim()) {
        allServices.push(`Other Service: ${otherServiceText}`);
      }

      const proceedingLabel = selectedProceeding === "other" ? `Other: ${otherProceedingText}` : selectedProceeding;

      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone);
      submitData.append("services", allServices.join(", "));
      submitData.append("proceeding_type", proceedingLabel);
      submitData.append("date", formData.date);
      submitData.append("time", formData.time);
      submitData.append("time_estimate", formData.timeEstimate);
      submitData.append("notes", formData.notes);
      submitData.append("nod_file", nodFile);

      await axios.post(`${API}/book-services`, submitData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Booking request submitted! We'll confirm your appointment shortly.");
      setFormData({ name: "", email: "", phone: "", date: "", time: "", timeEstimate: "", notes: "" });
      setSelectedServices([]); setSelectedCourtOptions([]); setSelectedProceeding("");
      setSelectedLanguages([]); setOtherServiceText(""); setOtherLanguageText(""); setOtherProceedingText("");
      setNodFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#0D0D0D] pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 relative overflow-hidden" data-testid="hire-hero">
        <div className="absolute inset-0 grid-pattern"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E8E8E8]/5 rounded-full blur-[150px]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-6"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 bg-[#E8E8E8] rounded-full animate-pulse-glow"></span>
                Professional Services
              </span>
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-none text-[#F5F5F7] mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }} data-testid="hire-heading">
              Hire a <span className="gradient-text">Court Reporter</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="text-lg text-[#E0E0E0] leading-relaxed">
              Book our certified court reporters directly for depositions, trials, hearings, and any legal proceeding requiring accurate verbatim transcription.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-section-dark" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Our Services</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              What We <span className="gradient-text">Offer</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: FileText, title: "Court Reporter", desc: "Certified court reporters for depositions, trials, hearings, and arbitrations." },
              { icon: CheckCircle, title: "Trial Readiness", desc: "Complete preparation services to ensure you're ready for trial day." },
              { icon: Mic, title: "Trial Tech", desc: "Technical support and equipment management for trial proceedings." },
              { icon: FileText, title: "Exhibit Tech", desc: "Professional exhibit management and presentation services." },
              { icon: Video, title: "Videographer", desc: "Professional legal videography for depositions and proceedings." },
              { icon: Globe, title: "Interpreter", desc: "Certified interpreters for multilingual depositions and proceedings." }
            ].map((service, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="bg-[#0D0D0D] border border-[#333333] p-6 card-glow rounded-xl">
                <service.icon className="w-10 h-10 text-[#E8E8E8] mb-4" />
                <h3 className="text-lg font-bold text-[#F5F5F7] mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>{service.title}</h3>
                <p className="text-[#E0E0E0] text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 bg-[#0D0D0D] border border-[#333333] p-8">
            <h3 className="text-lg font-bold text-[#F5F5F7] mb-6" style={{ fontFamily: 'Unbounded, sans-serif' }}>Court Reporter Options:</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {courtReportingOptions.map((option) => (
                <div key={option.id} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#38b6ff] rounded-full"></span>
                  <span className="text-[#E0E0E0] text-sm">{option.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-[#0D0D0D]" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Why Choose Us</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Professional <span className="gradient-text">Excellence</span>
              </h2>
              <p className="text-[#E0E0E0] leading-relaxed mb-8">
                When you book with Aym High Steno, you&apos;re hiring our team directly. No middlemen, no third-party coordination—just professional service from start to finish.
              </p>
              <ul className="space-y-4">
                {["NCRA Certified Court Reporters", "Real-Time Transcription Available", "Same-Day Rough Drafts", "Full Trial Support Services", "Nationwide Coverage"].map((item, index) => (
                  <motion.li key={index} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#38b6ff]" />
                    <span className="text-[#F5F5F7]">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="glass-card p-8">
              <div className="space-y-6">
                {[
                  { icon: Clock, color: "#38b6ff", title: "Quick Confirmation", desc: "We confirm bookings promptly" },
                  { icon: Phone, color: "#E8E8E8", title: "Direct Communication", desc: "Work directly with our team" },
                  { icon: Mail, color: "#38b6ff", title: "Easy Booking", desc: "Simple online scheduling" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: `${item.color}10`, border: `1px solid ${item.color}30` }}>
                      <item.icon className="w-6 h-6" style={{ color: item.color }} />
                    </div>
                    <div>
                      <p className="font-medium text-[#F5F5F7]">{item.title}</p>
                      <p className="text-sm text-[#B0B0B0]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section id="book" className="py-20 md:py-32 bg-section-alt relative overflow-hidden" data-testid="hire-booking">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#E8E8E8]/5 rounded-full blur-[150px]"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Get Started</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Book <span className="gradient-text">Services</span>
            </h2>
            <p className="text-[#B0B0B0]">Select your services and upload your Notice of Deposition to book.</p>
          </div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="bg-[#0D0D0D] border border-[#333333] p-8 lg:p-12">
            <form onSubmit={handleSubmit} className="space-y-8">
              <ServiceSelector
                selectedServices={selectedServices} onServiceToggle={toggleItem(setSelectedServices)}
                selectedCourtOptions={selectedCourtOptions} onCourtOptionToggle={toggleItem(setSelectedCourtOptions)}
                selectedLanguages={selectedLanguages} onLanguageToggle={toggleItem(setSelectedLanguages)}
                otherServiceText={otherServiceText} onOtherServiceChange={setOtherServiceText}
                otherLanguageText={otherLanguageText} onOtherLanguageChange={setOtherLanguageText}
              />

              <ProceedingSelector
                selected={selectedProceeding} onSelect={setSelectedProceeding}
                otherText={otherProceedingText} onOtherTextChange={setOtherProceedingText}
              />

              {/* Date, Time, Estimate */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { id: "date", label: "Date Requested *", type: "date", name: "date" },
                  { id: "time", label: "Time Requested *", type: "time", name: "time" },
                  { id: "timeEstimate", label: "Time Estimate *", type: "text", name: "timeEstimate", placeholder: "e.g., 2-3 hours" },
                ].map(field => (
                  <div key={field.id} className="space-y-2">
                    <Label htmlFor={field.id} className="text-[#F5F5F7] text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{field.label}</Label>
                    <Input id={field.id} name={field.name} type={field.type} placeholder={field.placeholder}
                      value={formData[field.name]} onChange={handleChange}
                      className="rounded-none bg-[#1A1A1A] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#E8E8E8] focus:ring-1 focus:ring-[#E8E8E8]"
                      required data-testid={`hire-form-${field.id === "timeEstimate" ? "estimate" : field.id}`} />
                  </div>
                ))}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { id: "name", label: "Contact Name *", type: "text", placeholder: "Jane Smith" },
                  { id: "phone", label: "Phone Number *", type: "tel", placeholder: "(555) 123-4567" },
                  { id: "email", label: "Email Address *", type: "email", placeholder: "jane@lawfirm.com" },
                ].map(field => (
                  <div key={field.id} className="space-y-2">
                    <Label htmlFor={field.id} className="text-[#F5F5F7] text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{field.label}</Label>
                    <Input id={field.id} name={field.id} type={field.type} placeholder={field.placeholder}
                      value={formData[field.id]} onChange={handleChange}
                      className="rounded-none bg-[#1A1A1A] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#E8E8E8] focus:ring-1 focus:ring-[#E8E8E8]"
                      required data-testid={`hire-form-${field.id}`} />
                  </div>
                ))}
              </div>

              <FileUploader nodFile={nodFile} fileInputRef={fileInputRef} onFileChange={handleFileChange} onRemoveFile={removeFile} />

              {/* Additional Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-[#F5F5F7] text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Additional Notes</Label>
                <Textarea id="notes" name="notes" placeholder="Any special requirements or additional information..."
                  value={formData.notes} onChange={handleChange} rows={3}
                  className="rounded-none bg-[#1A1A1A] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#E8E8E8] focus:ring-1 focus:ring-[#E8E8E8] resize-none"
                  data-testid="hire-form-notes" />
              </div>

              <Button type="submit"
                className="w-full rounded-none bg-[#E8E8E8] text-[#0D0D0D] hover:bg-[#E8E8E8]/90 btn-neon font-semibold py-6"
                style={{ fontFamily: 'JetBrains Mono, monospace' }} disabled={isLoading} data-testid="hire-form-submit">
                {isLoading ? "Submitting..." : "Book Services"}
              </Button>

              <p className="text-center text-[#E0E0E0] text-xs">* Required fields. We&apos;ll confirm your booking within one business day.</p>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
